import { environment } from "../../config/environment";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";
import { Loader2 } from "lucide-react";
import { customFetch } from "../../utils/Auth";
import SummaryRenderer from "./SummaryRenderer";

// True only when the API returned a summary with actual content. A template
// with no summary yet comes back as { summary: "", model: "" }, which we treat
// as "not generated" so the Generate Summary button is shown instead.
const hasSummaryContent = (summary) => {
  if (summary === null || summary === undefined) return false;
  if (typeof summary === "string") return summary.trim() !== "";
  if (Array.isArray(summary)) return summary.length > 0;
  if (typeof summary === "object") return Object.keys(summary).length > 0;
  return true;
};

// Polling configuration for async summary generation (re-fetch every 3s up to ~60s)
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 20;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Inline, read-only summary view rendered inside the template details dialog
// (mirrors the styling of the "Template Content" box).
const TemplateSummaryPanel = ({ name, version, type }) => {
  // SOAP note templates surface only the patient-context-pulled section of the
  // summary; all other summary cards are hidden for this type.
  const patientContextOnly = type === "SOAP_NOTE_TEMPLATE";

  const [loading, setLoading] = useState(false); // initial get-summary fetch
  const [error, setError] = useState(null); // fetch failure
  const [data, setData] = useState(null); // { summary, model } response
  const [generating, setGenerating] = useState(false); // generate + poll in progress
  const [generateError, setGenerateError] = useState(null); // generation failure/timeout

  // Guards against setting state after the panel unmounts (e.g. the user closes
  // the dialog or toggles back to the template view mid-fetch/mid-poll).
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const summaryUrl = `${environment.apiUrl}/template-summary/get-summary?name=${encodeURIComponent(
    name
  )}&version=${encodeURIComponent(version)}`;

  const fetchSummary = useCallback(async () => {
    if (!name || !version) return;
    try {
      setLoading(true);
      setError(null);
      setData(null);

      const response = await customFetch(summaryUrl, { method: "GET" });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (isMountedRef.current) setData(result);
    } catch (err) {
      console.error("Error fetching template summary:", err);
      if (isMountedRef.current) setError("Failed to fetch template summary");
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [name, version, summaryUrl]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // After kicking off generation, re-fetch until the summary appears.
  const pollForSummary = useCallback(async () => {
    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt += 1) {
      await sleep(POLL_INTERVAL_MS);
      if (!isMountedRef.current) return false;
      try {
        const response = await customFetch(summaryUrl, { method: "GET" });
        if (response.ok) {
          const result = await response.json();
          if (hasSummaryContent(result.summary)) {
            if (isMountedRef.current) setData(result);
            return true;
          }
        }
      } catch (err) {
        // Transient error while polling; keep trying until timeout
      }
    }
    return false;
  }, [summaryUrl]);

  // Fires when the user clicks "Generate Summary": kicks off generation
  // on the backend, then polls get-summary until the new summary appears (or 
  // time out).
  const handleGenerate = useCallback(async () => {
    if (!name || !version) return;
    try {
      setGenerating(true);
      setGenerateError(null);

      const response = await customFetch(
        `${environment.apiUrl}/template-summary/generate-summary`,
        {
          method: "POST",
          body: JSON.stringify({ name, version }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Generation is async on the backend, so wait for the summary to show up.
      const found = await pollForSummary();
      if (!found && isMountedRef.current) {
        setGenerateError(
          "Summary generation is taking longer than expected. Please check back shortly."
        );
      }
    } catch (err) {
      console.error("Error generating template summary:", err);
      if (isMountedRef.current) {
        setGenerateError("Failed to generate summary. Please try again.");
      }
    } finally {
      if (isMountedRef.current) setGenerating(false);
    }
  }, [name, version, pollForSummary]);

  const summaryExists = data && hasSummaryContent(data.summary);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-gray-500 block">
          Template Summary
        </label>
        {summaryExists && data.model && (
          <div className="flex items-center gap-1 text-sm">
            <span className="text-gray-500">Model:</span>
            <Badge
              variant="outline"
              className="text-xs border-gray-200 bg-gray-50 text-gray-700"
            >
              {data.model}
            </Badge>
          </div>
        )}
      </div>

      <div className="p-4 bg-gray-50 rounded-md border max-h-[60vh] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-ktx-purple"></div>
            <p className="ml-2 text-gray-600">Loading summary...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8 space-y-3">
            <p className="text-red-500">{error}</p>
            <Button onClick={fetchSummary} variant="outline" size="sm">
              Retry
            </Button>
          </div>
        ) : summaryExists ? (
          <SummaryRenderer
            value={data.summary}
            patientContextOnly={patientContextOnly}
          />
        ) : generating ? (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-ktx-purple" />
            <p className="text-gray-600">
              Generating summary... this may take up to a minute.
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <p className="text-gray-500">No summary has been generated yet.</p>
            {generateError && (
              <p className="text-sm text-red-500">{generateError}</p>
            )}
            <Button
              onClick={handleGenerate}
              variant="outline"
              size="sm"
              className="flex items-center gap-2 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400"
            >
              Generate Summary
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateSummaryPanel;
